<template>
  <Layout>
    <div class="fleet-task-instant-create">
      <MinimizeCreateSection />
      <MapLiveview />
    </div>
  </Layout>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import appConfig from '@/app.config'

import { Helper } from '@/mixin/helper'
import Layout from '@/router/layouts/main'
import MinimizeCreateSection from './sections/MinimizeCreateSection'
import MapLiveview from './sections/MapLiveview'

import { EventBus } from './lib/event-bus.js'
import {
  DEFAULT_POINT_TYPE,
  END_POINT_TYPE,
  START_POINT_TYPE,
  GOOGLE_MAP_ADDRESS_TYPE,
} from './lib/constants/types'
import * as FleetTaskInstant from '@/services/rest-api/lib/fleet-task-instant/fleet-task-instant'
import * as NearestVehicle from '@/services/rest-api/lib/fleet-task-instant/nearest-vehicle'

export default {
  page: {
    title: 'Quick Assignment',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    MinimizeCreateSection,
    MapLiveview,
  },
  mixins: [Helper],
  beforeRouteLeave(to, from, next) {
    this.$store.dispatch('fleetTaskInstant/resetMap')

    if (to.name !== 'Fleet Task Instant Detail') {
      this.$store.dispatch('fleetTaskInstant/resetSelectedPoints')
    }

    next()
  },
  computed: {
    ...mapState('fleetTaskInstant', [
      'selectedPoints',
      'pointType',
      'isLoadingGlobal',
      'formValues',
    ]),
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),

    hasUserGroupSuperUser() {
      const hasScope = this.hasAccessAll(['tms-user-group:read'])
      const isSuperUser = this.isSuperUser()

      return hasScope && isSuperUser
    },
  },
  created() {
    // listen to create event
    EventBus.$on('create-fleet-task-instant', this.createFleetTaskInstant)

    // Entry point select address from liveview
    if (!this.$route.query.address) return

    const firstPoint = { ...this.$route.query, type: GOOGLE_MAP_ADDRESS_TYPE }

    firstPoint.lat = Number(firstPoint.lat)
    firstPoint.lng = Number(firstPoint.lng)
    delete firstPoint.address

    // set first point
    this.$store.dispatch('fleetTaskInstant/setSelectedPoints', { index: 0, value: firstPoint })
  },
  async beforeMount() {
    if (!this.hasUserGroupSuperUser) {
      await this.getAggregateFleet()
    }
  },
  beforeDestroy() {
    this.$store.dispatch('fleetTaskInstant/resetAllState')
    EventBus.$off('create-fleet-task-instant', this.createFleetTaskInstant)
  },
  methods: {
    async getAggregateFleet() {
      if (this.isLoadingGlobal) return
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', true)
      const response = await NearestVehicle.get({
        latitude: 0,
        longitude: 0,
      })

      if (!response?.data?.result) {
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
        this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
        return
      }

      const objVehicles = {}
      response.data.result.forEach((vehicle) => {
        this.$set(objVehicles, vehicle.id, {
          ...vehicle,
        })
      })

      this.$store.dispatch('fleetTaskInstant/setListVehicle', objVehicles)
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
    },
    async createFleetTaskInstant(vehicle) {
      if (this.isLoadingGlobal) return
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', true)

      const points = []
      if (this.selectedPoints.length > 0) {
        this.selectedPoints.forEach((point, index) => {
          if (point.id === undefined) return

          const address = {
            id: point.id,
            type: point.type,
            name: point.name || point.fullName,
            full_name: point.fullName,
            lat: point.lat,
            lng: point.lng,
          }

          // don't assign id if it's number in string
          if (point.type === GOOGLE_MAP_ADDRESS_TYPE && !isNaN(Number(point.id))) {
            delete address.id
          }

          let type = DEFAULT_POINT_TYPE
          if (index === 0 && this.pointType.start) {
            type = START_POINT_TYPE
          }
          if (index === this.selectedPoints.length - 1 && this.pointType.end) {
            type = END_POINT_TYPE
          }

          points.push({ type, address })
        })
      }

      let data = {
        vehicle_id: vehicle.id,
      }

      if (vehicle?.driver?.id && vehicle?.driver?.is_available) {
        data.driver_id = vehicle.driver.id
      }

      if (this.hasUserGroupSuperUser && this.formValues.user_group_id !== null) {
        data.user_group_id = Number(this.formValues.user_group_id)
      }

      const formData = {
        ...data,
        points: points.length > 0 ? points : undefined,
        send_late_notification:
          JSON.parse(sessionStorage.getItem('quickAssignmentLateNotification')) ?? true,
      }

      const res = await FleetTaskInstant.store(formData)

      if (res.errors) {
        this.$mcToast.error(this.$t('global.somethingWentWrong'))
        return
      }

      this.$router.push({ name: 'Fleet Task Instant Detail', params: { id: res.data.id } })
      this.$store.dispatch('fleetTaskInstant/setIsLoadingGlobal', false)
    },
  },
}
</script>

<style lang="scss" scoped>
.fleet-task-instant-create {
  position: relative;
  display: flex;
  gap: 20px;
}
</style>

<style lang="scss">
@import '@/design/custom/pages/_fleettaskinstant.scss';
</style>
